import { useEffect } from "react";
import { Smile, Reply, Copy, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

type MobileChatActionsProps = {
  open: boolean;
  onClose: () => void;
  canDelete: boolean;
  plainText: string;
  onReact?: () => void;
  onReply?: () => void;
  onDelete?: () => void;
};

export function MobileChatActions({
  open,
  onClose,
  canDelete,
  plainText,
  onReact,
  onReply,
  onDelete,
}: MobileChatActionsProps) {
  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
    } catch {
      // silent fallback
    }
    onClose();
  };

  const actions: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    onClick: () => void;
    destructive?: boolean;
    show: boolean;
  }[] = [
    { icon: Smile, label: "React", onClick: () => { onReact?.(); onClose(); }, show: !!onReact },
    { icon: Reply, label: "Reply", onClick: () => { onReply?.(); onClose(); }, show: !!onReply },
    { icon: Copy, label: "Copy", onClick: handleCopy, show: true },
    {
      icon: Trash2,
      label: "Delete",
      onClick: () => { onDelete?.(); onClose(); },
      destructive: true,
      show: canDelete && !!onDelete,
    },
  ];

  const visibleActions = actions.filter((a) => a.show);

  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 animate-in fade-in duration-200"
        onClick={onClose}
      />
      {/* Action sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-2xl p-4 pb-safe animate-in slide-in-from-bottom duration-200">
        <div className="w-10 h-1 rounded-full bg-muted mx-auto mb-4" />
        <div className="space-y-1">
          {visibleActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                  "active:bg-muted",
                  action.destructive
                    ? "text-destructive"
                    : "text-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
                {action.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full mt-2 py-3 rounded-xl text-sm font-medium text-muted-foreground active:bg-muted transition-colors border-t border-border"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
